// Sales → Billing run: one month's invoice for every paying organisation,
// previewed (sales_billing_preview), then issued together (sales_issue_month).
// Only a month that has ended can be issued; an organisation already invoiced
// for it, or with nothing to bill, is skipped by the server too.

import { useState } from "react";
import { fill } from "../../i18n/dictionary";
import { previousMonth, type BillingPreviewRow } from "../../data/sales";
import { Button, Card, Chip, ConfirmDialog, ErrorPanel, Field, LoadingLine, Notice, TableCard, TD, TH, formatMoney, formatMonth, formatNumber, useLoad } from "../admin/ui";
import { SectionTitle, salesErrorCopy, statusChip, useSales } from "./shared";

/** The rows sales_issue_month will issue. */
export function toIssue(rows: BillingPreviewRow[]): BillingPreviewRow[] {
  return rows.filter((r) => !r.alreadyInvoiced && r.amount > 0);
}

/** Sums per currency, so a mixed list never adds ringgit to dollars. */
export function sumByCurrency(rows: BillingPreviewRow[], pick: (r: BillingPreviewRow) => number): [string, number][] {
  const m = new Map<string, number>();
  for (const r of rows) m.set(r.currency, Math.round(((m.get(r.currency) ?? 0) + pick(r)) * 100) / 100);
  return [...m.entries()];
}

export function BillingRun() {
  const { ds, t, can, locale } = useSales();
  const maxMonth = previousMonth();
  const [month, setMonth] = useState(maxMonth);
  const valid = /^\d{4}-\d{2}$/.test(month) && month <= maxMonth;
  const load = useLoad(() => (valid ? ds.billingPreview(`${month}-01`) : Promise.resolve([])), [ds, month, valid]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const rows = load.data ?? [];
  const issuing = toIssue(rows);
  const money = (pairs: [string, number][]) => pairs.map(([c, n]) => formatMoney(locale, n, c)).join(" + ") || formatMoney(locale, 0, "MYR");

  async function issue() {
    setBusy(true);
    setError("");
    try {
      const n = await ds.issueMonth(`${month}-01`);
      setConfirming(false);
      setDone(fill(t.billing.issued, { n }));
      await load.refresh();
    } catch (err) {
      setError(salesErrorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="billing-title">
      <SectionTitle id="billing-title">{t.billing.title}</SectionTitle>
      <p style={{ margin: "0 0 14px", color: "var(--ink-soft)", fontSize: 15, maxWidth: 720 }}>{t.billing.intro}</p>
      <div style={{ maxWidth: 240, marginBottom: 14 }}>
        <Field
          label={t.billing.month}
          type="month"
          max={maxMonth}
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            setDone("");
          }}
        />
      </div>

      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : load.error ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : rows.length === 0 ? (
        <Card>
          <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.billing.none}</p>
        </Card>
      ) : (
        <>
          <TableCard label={fill(t.billing.confirmTitle, { month: formatMonth(locale, month) })} minWidth={820}>
            <thead>
              <tr>
                <th scope="col" style={TH}>{t.billing.colOrg}</th>
                <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.billing.colLm}</th>
                <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.billing.colSubtotal}</th>
                <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.billing.colSst}</th>
                <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.billing.colTotal}</th>
                <th scope="col" style={TH}>{t.billing.colState}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = statusChip(t, r.status);
                const state = r.alreadyInvoiced
                  ? { tone: "muted" as const, label: t.billing.alreadyInvoiced }
                  : r.amount > 0
                    ? { tone: "info" as const, label: t.billing.toIssue }
                    : { tone: "neutral" as const, label: t.billing.nothingToBill };
                return (
                  <tr key={r.orgId}>
                    <th scope="row" style={{ ...TD, textAlign: "start", fontWeight: 600 }}>
                      {r.orgName}
                      {r.status !== "active" && (
                        <span style={{ marginInlineStart: 8 }}>
                          <Chip tone={st.tone}>{st.label}</Chip>
                        </span>
                      )}
                    </th>
                    <td style={{ ...TD, textAlign: "end" }}>{formatNumber(locale, r.licenceMonths)}</td>
                    <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap" }}>{formatMoney(locale, r.subtotal, r.currency)}</td>
                    <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap" }}>{formatMoney(locale, r.sstAmount, r.currency)}</td>
                    <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", fontWeight: 700 }}>{formatMoney(locale, r.amount, r.currency)}</td>
                    <td style={TD}>
                      <Chip tone={state.tone}>{state.label}</Chip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" style={{ ...TD, textAlign: "start" }}>{t.billing.totals}</th>
                <td style={{ ...TD, textAlign: "end" }}>{formatNumber(locale, issuing.reduce((s, r) => s + r.licenceMonths, 0))}</td>
                <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap" }}>{money(sumByCurrency(issuing, (r) => r.subtotal))}</td>
                <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap" }}>{money(sumByCurrency(issuing, (r) => r.sstAmount))}</td>
                <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", fontWeight: 700 }}>{money(sumByCurrency(issuing, (r) => r.amount))}</td>
                <td style={TD}>
                  <Chip tone="info">{t.billing.toIssue}</Chip>
                </td>
              </tr>
            </tfoot>
          </TableCard>

          <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {can("invoices") &&
              (issuing.length > 0 ? (
                <Button onClick={() => setConfirming(true)}>
                  {issuing.length === 1 ? t.billing.issueOne : fill(t.billing.issueN, { n: issuing.length })}
                </Button>
              ) : (
                <span style={{ color: "var(--ink-soft)" }}>{t.billing.nothing}</span>
              ))}
            <span role="status" aria-live="polite" style={{ fontWeight: 600, color: "var(--sage-deep)" }}>
              {done}
            </span>
          </div>
          {!can("invoices") && (
            <div style={{ marginTop: 10 }}>
              <Notice tone="neutral">{t.shell.readOnly}</Notice>
            </div>
          )}
        </>
      )}

      {confirming && (
        <ConfirmDialog
          title={fill(t.billing.confirmTitle, { month: formatMonth(locale, month) })}
          body={fill(t.billing.confirmBody, { n: issuing.length, total: money(sumByCurrency(issuing, (r) => r.amount)) })}
          confirmLabel={issuing.length === 1 ? t.billing.issueOne : fill(t.billing.issueN, { n: issuing.length })}
          cancelLabel={t.common.cancel}
          busy={busy}
          error={error}
          onConfirm={() => void issue()}
          onCancel={() => {
            setConfirming(false);
            setError("");
          }}
        />
      )}
    </section>
  );
}
